import { Children, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { cn } from "@/lib/utils";
import { localizeHref } from "@/paraglide/runtime.js";
import { blogSlugify } from "@complete-athlete/enums";

/** The `## ` headings of a document, for its table of contents. */
export const legalSections = (source: string) =>
  [...source.matchAll(/^## (.+)$/gm)].map(([, title = ""]) => ({ id: blogSlugify(title), title }));

const linkTarget = (href: string | undefined) => (href?.startsWith("/") ? localizeHref(href) : href);
const opensElsewhere = (href: string | undefined) => Boolean(href && /^https?:/.test(href));

const linkClass = "font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary";

const COMPONENTS: Components = {
  h2: ({ children }) => (
    <h2
      id={blogSlugify(Children.toArray(children).join(""))}
      className="mt-12 mb-4 scroll-mt-6 text-xl font-medium tracking-[-0.02em] text-balance text-foreground first:mt-0"
    >
      {children}
    </h2>
  ),
  p: ({ children }) => <p className="my-4 text-pretty">{children}</p>,
  ul: ({ children }) => <ul className="my-4 list-disc space-y-2 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal space-y-2 pl-5">{children}</ol>,
  strong: ({ children }) => <strong className="font-medium text-foreground">{children}</strong>,
  code: ({ children }) => <code className="rounded bg-muted px-1 font-mono text-[0.9em]">{children}</code>,
  a: ({ href, children }) => (
    <a
      href={linkTarget(href)}
      {...(opensElsewhere(href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={linkClass}
    >
      {children}
    </a>
  ),
};

/** A legal document, set for reading. Raw HTML never renders. */
export const LegalMarkdown = ({ source, className }: { source: string; className?: string }) => (
  <div className={cn("text-[15px] leading-7 text-foreground/80", className)}>
    <ReactMarkdown skipHtml components={COMPONENTS}>
      {source}
    </ReactMarkdown>
  </div>
);

const INLINE_COMPONENTS: Components = {
  p: ({ children }: { children?: ReactNode }) => <>{children}</>,
  // Beside a half-filled form, a document opens in a new tab so nothing typed is lost.
  a: ({ href, children }) => (
    <a
      href={linkTarget(href)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="underline underline-offset-4 hover:text-primary"
    >
      {children}
    </a>
  ),
};

/** One sentence with links, for a label. */
export const LegalInline = ({ source }: { source: string }) => (
  <ReactMarkdown skipHtml components={INLINE_COMPONENTS}>
    {source}
  </ReactMarkdown>
);
